import React, { lazy } from 'react';
import { Ruler, ShieldCheck, Settings, Wrench, CheckCircle2, Sun, Expand, Eye, Accessibility, Sparkles, Droplets } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
export function Quality() {
  const features = [{
    title: 'Custom Fit',
    copy: 'Every panel is measured and fabricated for your opening instead of relying on a one-size-fits-most shower door.',
    icon: Ruler
  }, {
    title: 'Tempered Safety Glass',
    copy: 'Shower glass is built from durable tempered glass designed for strength, safety and long-term use.',
    icon: ShieldCheck
  }, {
    title: 'Better Hardware Details',
    copy: 'Hinges, clips, handles, towel bars and brackets should look good now and hold up under everyday bathroom use.',
    icon: Settings
  }, {
    title: 'Clean Installation',
    copy: 'Professional installation helps avoid poor fit, excess caulk, uneven gaps and other issues that make a shower look unfinished.',
    icon: Wrench
  }];
  return <section className="py-20 bg-gray-50" data-id="element-211">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-212">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-213">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-214">
            Quality Glass, Quality Hardware & Professional Installation
          </h2>
          <p className="text-lg text-gray-600" data-id="element-215">
            A great-looking glass shower enclosure depends on three things: the
            right glass, the right hardware and the right installation. Kosin’s
            Glass focuses on custom measurements, premium tempered glass,
            quality hardware, clean details and professional installation so
            your shower looks finished and functions properly.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-216">
          {features.map((feature, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-217">
              <div className="w-12 h-12 bg-glass rounded-xl flex items-center justify-center mb-6 text-primary" data-id="element-218">
                <feature.icon className="w-6 h-6" data-id="element-219" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-220">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-221">{feature.copy}</p>
            </FadeIn>)}
        </div>

        <FadeIn className="text-center" data-id="element-222">
          <a href="#quote-form" className="inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-semibold py-4 px-8 rounded-xl transition-colors" data-id="element-223">
            Get a Professional Shower Door Quote
          </a>
        </FadeIn>
      </div>
    </section>;
}
export function Checklist() {
  const items = ['Choose a company that comes out to inspect the bathroom and take proper measurements', 'Ask whether installers are experienced glass professionals', 'Finalize the quote after exact measurements and product selections', 'Confirm the company can custom cut glass and hardware to fit the opening', 'Ask about glass options, hardware finishes and installation details', 'Ask what caulk is used; a clean, mildew-resistant, non-shrinking caulk is preferred', 'Avoid companies that rely on excess caulk to hide gaps', 'Ask about the installation guarantee before work begins'];
  return <section className="py-20 bg-white" data-id="element-224">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-225">
        <FadeIn className="max-w-3xl mx-auto text-center mb-16" data-id="element-226">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-227">
            What to Look for Before Hiring a Shower Door Installer
          </h2>
          <p className="text-lg text-gray-600" data-id="element-228">
            The current page gives homeowners several practical tips. Turn this
            into a scannable checklist.
          </p>
        </FadeIn>

        <div className="max-w-4xl mx-auto bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-100 mb-12" data-id="element-229">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-230">
            {items.map((item, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-4" data-id="element-231">
                <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" data-id="element-232" />
                <span className="text-gray-700 font-medium leading-relaxed" data-id="element-233">
                  {item}
                </span>
              </FadeIn>)}
          </div>
        </div>

        <FadeIn className="text-center" data-id="element-234">
          <a href="#quote-form" className="inline-flex items-center justify-center bg-navy hover:bg-navy-800 text-white font-semibold py-4 px-8 rounded-xl transition-colors" data-id="element-235">
            Schedule an In-Home Measurement
          </a>
        </FadeIn>
      </div>
    </section>;
}
export function Process() {
  const steps = [{
    title: 'Request Your Free In-Home Quote',
    copy: 'Submit the short form or call Kosin’s Glass.'
  }, {
    title: 'Share Photos & Basic Details',
    copy: 'A team member will ask about your bathroom layout, existing shower or tub opening, project timeline, rough measurements and style preferences.'
  }, {
    title: 'In-Home Consultation & Samples',
    copy: 'Kosin’s Glass can come to your home, review the space, show samples, discuss glass options and recommend the right enclosure style.'
  }, {
    title: 'Exact Measurements & Firm Quote',
    copy: 'Exact measurements are taken before fabrication. Your final quote should reflect the measurements, glass selection, hardware finish and installation requirements.'
  }, {
    title: 'Fabrication & Professional Installation',
    copy: 'Your custom shower glass is prepared, then installed by experienced glass technicians with attention to fit, finish, function and long-term appearance.'
  }];
  return <section className="py-20 bg-glass" data-id="element-236">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-237">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-238">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-239">
            How Your Custom Shower Door Quote Works
          </h2>
        </FadeIn>

        <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-240">
          <div className="lg:col-span-8" data-id="element-241">
            <div className="space-y-8" data-id="element-242">
              {steps.map((step, i) => <FadeIn key={i} delay={i * 0.1} className="flex gap-6 bg-white p-6 rounded-2xl shadow-sm border border-blue-50" data-id="element-243">
                  <div className="w-12 h-12 shrink-0 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold" data-id="element-244">
                    {i + 1}
                  </div>
                  <div data-id="element-245">
                    <h3 className="text-xl font-bold text-navy mb-2" data-id="element-246">
                      {step.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-247">{step.copy}</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>

          <div className="lg:col-span-4" data-id="element-248">
            <FadeIn delay={0.5} className="bg-navy text-white p-8 rounded-3xl sticky top-8" data-id="element-249">
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-250">
                <Wrench className="w-6 h-6 text-primary" data-id="element-251" />
                Remodeling tip
              </h4>
              <p className="text-blue-100 leading-relaxed" data-id="element-252">
                If your bathroom is being remodeled, contact Kosin’s Glass early
                for planning help. Final shower glass measurements are typically
                best after tile, walls and curb details are complete.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>;
}
export function Benefits() {
  const benefits = [{
    title: 'Brighter Bathroom',
    copy: 'Glass allows light to move through the space, helping bathrooms feel more open and inviting.',
    icon: Sun,
    image: 'https://cdn.magicpatterns.com/uploads/rqV7DnYYNfKLA7mQahLzQj/BrighterBathroom.png',
    imageAlt: 'Bright, sunlit bathroom with frameless glass shower enclosure installed by Kosin’s Glass'
  }, {
    title: 'Larger Visual Feel',
    copy: 'Clear shower glass avoids visual barriers and can make smaller bathrooms feel less confined.',
    icon: Expand,
    image: 'https://cdn.magicpatterns.com/uploads/45kN8wDFyWK8RuWd87amjZ/Larger_Visual_Feel.png',
    imageAlt: 'Open, airy bathroom with frameless walk-in glass shower that makes the space feel larger'
  }, {
    title: 'Shows Off Tile & Stone',
    copy: 'Frameless and clear glass designs let your tile, stone, fixtures and shower details remain visible.',
    icon: Eye,
    image: 'https://cdn.magicpatterns.com/uploads/cuXuKgU6x36z7MrUpH4j2U/Shows_Off_Tile__Stone.png',
    imageAlt: 'Frameless glass shower showcasing pebble floor tile, stone niche and matte black hardware by Kosin’s Glass'
  }, {
    title: 'Easier Access Options',
    copy: 'Walk-in, low-curb and barrier-free shower glass options can support easier entry and daily use.',
    icon: Accessibility,
    image: 'https://cdn.magicpatterns.com/uploads/wVkywk9UvyQ8sk9AiEVjv3/Easier_Access_Options.png',
    imageAlt: 'Curbless walk-in shower with single fixed glass panel for barrier-free access by Kosin’s Glass'
  }, {
    title: 'Easier Cleaning',
    copy: 'Smooth glass surfaces and optional protective coatings help reduce maintenance compared with older framed doors or shower curtains.',
    icon: Sparkles,
    image: 'https://cdn.magicpatterns.com/uploads/wDp3rzmVERFnmnNwBpJzS8/Easier_Cleaning.png',
    imageAlt: 'Hand wiping down a frameless glass shower door for easy cleaning'
  }, {
    title: 'Spa-Like Style',
    copy: 'A custom glass shower can turn an ordinary bathroom into a cleaner, more modern and more relaxing space.',
    icon: Droplets,
    image: 'https://cdn.magicpatterns.com/uploads/r5mwumAigP95GbBYuSVv8p/Spa-Like_Style.png',
    imageAlt: 'Warm, spa-like bathroom with frameless glass walk-in shower, soaking tub and natural light'
  }];
  return <section className="py-20 bg-white" data-id="element-253">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-254">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-255">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-256">
            Why Homeowners Choose Glass Shower Enclosures
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-257">
          {benefits.map((benefit, i) => <FadeIn key={i} delay={i * 0.1} className="bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-100 transition-colors overflow-hidden flex flex-col" data-id="element-258">
              {benefit.image && <div className="aspect-[16/10] overflow-hidden bg-gray-100" data-id="element-259">
                  <img src={benefit.image} alt={benefit.imageAlt} className="w-full h-full object-cover" loading="lazy" data-id="element-260" />
                </div>}
              <div className="p-8 flex-1" data-id="element-261">
                {!benefit.image && <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 text-primary shadow-sm" data-id="element-262">
                    <benefit.icon className="w-6 h-6" data-id="element-263" />
                  </div>}
                {benefit.image && <div className="inline-flex items-center gap-2 mb-4 text-primary" data-id="element-264">
                    <benefit.icon className="w-5 h-5" data-id="element-265" />
                    <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-266">
                      Benefit
                    </span>
                  </div>}
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-267">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-268">{benefit.copy}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}